/**
 * Recorded locators — finding an element again without a developer's tag (G19).
 *
 * The recorder builds one when the author clicks or fills something; the walkthrough
 * overlay resolves it when a reader plays the guide back. Both use the same lookup
 * order, because the most stable fact about an element should win both times:
 *
 *   `data-gms-target` (a tag, when the screen has one) → role + accessible name →
 *   label → placeholder → `data-testid` → visible text → a CSS path.
 *
 * **Nothing here reads what the user typed.** A field is described by its label, never
 * its value, and an element inside `[data-support-redact]` contributes no words at all.
 * The server re-validates the shape (`gms/services/locators.py`) and rejects any key that
 * could carry a value.
 *
 * **Nor what the screen shows about a person.** Recording happens on live data (G20), and
 * a row's link or a list option is usually *data* — "Jane Doe · 555-0100" — not interface
 * text. So a list item contributes no words (it is found by position, and the step says
 * "the highlighted item"), and any words that look like an email or a phone number are
 * dropped wherever they come from. The server refuses such words too, and Review & Edit
 * can strip every word from a step as a last resort.
 *
 * Resolution never guesses: each finder is tried in turn and the first one that matches
 * exactly one visible element wins. If none does, the step reports that it cannot be
 * found rather than spotlighting something that might be wrong.
 */

import type { RecordedAction, RecordedLocator } from '../types';

/** Marks every piece of GMS chrome, so the recorder ignores it and captures leave it out. */
export const GMS_UI_ATTR = 'data-gms-ui';

/** Things a click on which is worth recording. */
const CLICKABLE = [
  'button',
  'a[href]',
  'summary',
  'label',
  '[data-gms-target]',
  'input[type="checkbox"]',
  'input[type="radio"]',
  'input[type="button"]',
  'input[type="submit"]',
  '[role="button"]',
  '[role="link"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="switch"]',
].join(', ');

/** Fields whose change is a "fill" step. */
const FILLABLE = [
  'textarea',
  'input:not([type])',
  ...['text', 'email', 'search', 'tel', 'url', 'number', 'date', 'time', 'datetime-local', 'password'].map(
    (type) => `input[type="${type}"]`
  ),
].join(', ');

/** Elements carrying each implicit role, for role + name lookup. */
const ROLE_SELECTORS: Record<string, string> = {
  button: 'button, summary, input[type="button"], input[type="submit"], [role="button"]',
  link: 'a[href], [role="link"]',
  textbox: `${FILLABLE}, [role="textbox"]`,
  combobox: 'select, [role="combobox"]',
  checkbox: 'input[type="checkbox"], [role="checkbox"]',
  radio: 'input[type="radio"], [role="radio"]',
};

const MAX_WORDS = 80;

function words(value: string | null | undefined, limit = MAX_WORDS): string {
  return (value ?? '').replace(/\s+/g, ' ').trim().slice(0, limit);
}

function isRedacted(element: Element): boolean {
  return Boolean(element.closest('[data-support-redact]'));
}

/** Containers whose items are records, not controls. Same idea as the server's check. */
const LIST_ITEM =
  'tr, li, [role="row"], [role="gridcell"], [role="listitem"], [role="option"], [role="treeitem"]';

/** Whether an element is (inside) one item of a list or table — so its words are data. */
export function isListItem(element: Element): boolean {
  return Boolean(element.closest(LIST_ITEM));
}

const EMAIL = /[^\s@]+@[^\s@]+\.[^\s@]+/;
/** Seven or more digits, allowing the separators phone numbers are written with. */
const PHONE = /(?:\d[\s().+-]*){7,}/;

/** Mirrors `looks_personal` in `gms/services/locators.py`. */
function looksPersonal(value: string): boolean {
  return EMAIL.test(value) || PHONE.test(value);
}

/** Words fit to store: none from a list item, none that look like contact details. */
function storable(value: string, element: Element): string {
  if (!value || isListItem(element) || looksPersonal(value)) return '';
  return value;
}

function isVisible(element: Element): boolean {
  return element.getClientRects().length > 0;
}

/** Whether an event target belongs to GMS itself (drawer, overlay, recorder chrome). */
export function isGmsUi(element: Element): boolean {
  return Boolean(element.closest(`[${GMS_UI_ATTR}]`));
}

/** The element a pointer-down should be recorded against, or null to ignore it. */
export function clickableFor(element: Element): HTMLElement | null {
  if (element.closest(`${FILLABLE}, select`)) return null; // recorded on change instead
  const clickable = element.closest<HTMLElement>(CLICKABLE);
  if (!clickable) return null;
  // A label that wraps or names a text field just focuses it; the change records it.
  if (clickable.tagName === 'LABEL') {
    const control = (clickable as HTMLLabelElement).control;
    if (!control || control.matches(`${FILLABLE}, select`)) return null;
    return control;
  }
  return clickable;
}

/** Whether an element is a field whose change is a fill or a selection. */
export function fieldAction(element: Element): RecordedAction | null {
  if (element.matches('select')) return 'select';
  if (element.matches(FILLABLE)) return 'fill';
  return null;
}

function roleOf(element: Element): string {
  const explicit = element.getAttribute('role');
  if (explicit) return explicit;
  for (const [role, selector] of Object.entries(ROLE_SELECTORS)) {
    if (element.matches(selector)) return role;
  }
  return '';
}

/** A label's own words, without the text of any control nested inside it. */
function labelWords(label: Element): string {
  const copy = label.cloneNode(true) as Element;
  copy.querySelectorAll('input, select, textarea, option').forEach((node) => node.remove());
  return words(copy.textContent);
}

/** The words that name a field or control: aria-label, its label, or its title. */
function labelFor(element: Element): string {
  const aria = element.getAttribute('aria-label');
  if (aria) return words(aria);
  const labelledBy = element.getAttribute('aria-labelledby');
  if (labelledBy) {
    const text = labelledBy
      .split(/\s+/)
      .map((id) => document.getElementById(id)?.textContent ?? '')
      .join(' ');
    if (words(text)) return words(text);
  }
  const labels = (element as HTMLInputElement).labels;
  if (labels && labels.length) return labelWords(labels[0]);
  return words(element.getAttribute('title'));
}

/**
 * An element's accessible name: its label, or for buttons and links the words printed
 * on them. Never a field's value.
 */
function accessibleName(element: Element): string {
  if (isRedacted(element)) return '';
  const label = labelFor(element);
  if (label) return label;
  if (element.matches(`${FILLABLE}, select`)) return '';
  return words(element.textContent) || words(element.querySelector('img')?.getAttribute('alt'));
}

/** The server's limit on a stored CSS path (`gms/services/locators.py`). */
const MAX_CSS = 500;

/**
 * A CSS path that matches only this element — or '' when there is none short enough.
 *
 * Built upwards from the element, stopping at the first ancestor (or stable id) at which
 * the path already matches exactly this one element, so it stays short. It is never cut
 * to fit: a truncated selector is a different selector, which can match an ancestor or
 * another control, and resolution would then spotlight the wrong thing.
 */
function cssPath(element: Element): string {
  const parts: string[] = [];
  let node: Element | null = element;
  while (node && node !== document.body) {
    const id = node.getAttribute('id');
    // Generated ids (React's ":r1:", numbers) change between renders; skip them.
    if (id && /^[A-Za-z][\w-]*$/.test(id)) {
      parts.unshift(`#${CSS.escape(id)}`);
      break;
    }
    const parent: Element | null = node.parentElement;
    const tag = node.tagName.toLowerCase();
    if (parent) {
      const same = Array.from(parent.children).filter((child) => child.tagName === node!.tagName);
      parts.unshift(same.length > 1 ? `${tag}:nth-of-type(${same.indexOf(node) + 1})` : tag);
    } else {
      parts.unshift(tag);
    }
    if (uniquelyMatches(parts.join(' > '), element)) break;
    node = parent;
  }
  const path = parts.join(' > ');
  if (path.length > MAX_CSS || !uniquelyMatches(path, element)) return '';
  return path;
}

function uniquelyMatches(selector: string, element: Element): boolean {
  try {
    const matches = document.querySelectorAll(selector);
    return matches.length === 1 && matches[0] === element;
  } catch {
    return false;
  }
}

/** Whether a locator has any way to find its element (the server requires one). */
export function hasFinder(locator: RecordedLocator): boolean {
  return Boolean(
    locator.gms_target ||
      (locator.role && locator.name) ||
      locator.label ||
      locator.placeholder ||
      locator.testid ||
      locator.text ||
      locator.css
  );
}

/** Describe `element` well enough to find it again on `route`. */
export function buildLocator(
  element: Element,
  route: string,
  action: RecordedAction
): RecordedLocator {
  const locator: RecordedLocator = { route, action };
  // A tag is used only when it identifies this one control: on the element itself, or
  // on a wrapper holding no other control. A tagged section around several fields would
  // otherwise let any of them complete the step on playback.
  const tagged = element.closest('[data-gms-target]');
  if (
    tagged &&
    (tagged === element ||
      tagged.querySelectorAll(`${CLICKABLE}, ${FILLABLE}, select`).length <= 1)
  ) {
    locator.gms_target = tagged.getAttribute('data-gms-target') ?? undefined;
  }
  const role = roleOf(element);
  const name = storable(accessibleName(element), element);
  if (role && name) {
    locator.role = role;
    locator.name = name;
  }
  if (element.matches(`${FILLABLE}, select`) && !isRedacted(element)) {
    const label = storable(labelFor(element), element);
    if (label) locator.label = label;
    const placeholder = storable(words(element.getAttribute('placeholder'), 200), element);
    if (placeholder) locator.placeholder = placeholder;
  }
  const testid = element.getAttribute('data-testid');
  if (testid) locator.testid = testid.slice(0, 200);
  if (!role && !isRedacted(element)) {
    const text = storable(words(element.textContent), element);
    if (text) locator.text = text;
  }
  const css = cssPath(element);
  if (css) locator.css = css;
  return locator;
}

/**
 * "Click "Add Guest"", "Fill in Email", "Choose a value in BPM Name". A list item has no
 * stored words (see the header), so its step names the highlighted item instead.
 */
export function suggestStepText(locator: RecordedLocator, fromList = false): string {
  if (fromList && locator.action === 'click') {
    return 'Click an item in the list — the highlighted one is an example.';
  }
  const what = (locator.name || locator.label || locator.placeholder || locator.text || '')
    // "Search…" or "Name:" read badly with the sentence's own full stop after them.
    .replace(/[\s.:…]+$/u, '');
  switch (locator.action) {
    case 'fill':
      return what ? `Fill in ${what}.` : 'Fill in the highlighted field.';
    case 'select':
      return what ? `Choose a value in ${what}.` : 'Choose a value in the highlighted list.';
    case 'click':
      return what ? `Click "${what}".` : 'Click the highlighted item.';
    default:
      return what ? `Look at ${what}.` : 'Look at the highlighted area.';
  }
}

function single(candidates: Iterable<Element>): HTMLElement | null {
  const visible = Array.from(candidates).filter(
    (element) => isVisible(element) && !isGmsUi(element)
  );
  return visible.length === 1 ? (visible[0] as HTMLElement) : null;
}

function attr(name: string, value: string): string {
  return `[${name}="${CSS.escape(value)}"]`;
}

/**
 * The element a locator describes on the current screen, or null when no finder picks
 * out exactly one. Never a guess.
 */
export function resolveLocator(locator: RecordedLocator): HTMLElement | null {
  const finders: Array<() => HTMLElement | null> = [
    () =>
      locator.gms_target
        ? single(document.querySelectorAll(attr('data-gms-target', locator.gms_target)))
        : null,
    () => {
      if (!locator.role || !locator.name) return null;
      const selector = ROLE_SELECTORS[locator.role] ?? attr('role', locator.role);
      return single(
        Array.from(document.querySelectorAll(selector)).filter(
          (element) => accessibleName(element) === locator.name
        )
      );
    },
    () =>
      locator.label
        ? single(
            Array.from(document.querySelectorAll(`${FILLABLE}, select`)).filter(
              (element) => labelFor(element) === locator.label
            )
          )
        : null,
    () =>
      locator.placeholder
        ? single(document.querySelectorAll(attr('placeholder', locator.placeholder)))
        : null,
    () =>
      locator.testid ? single(document.querySelectorAll(attr('data-testid', locator.testid))) : null,
    () =>
      locator.text
        ? single(
            Array.from(document.querySelectorAll(CLICKABLE)).filter(
              (element) => words(element.textContent) === locator.text
            )
          )
        : null,
    () => {
      if (!locator.css) return null;
      try {
        return single(document.querySelectorAll(locator.css));
      } catch {
        return null;
      }
    },
  ];
  for (const find of finders) {
    const found = find();
    if (found) return found;
  }
  return null;
}
